import {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useState,
  useMemo,
} from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import { playTick, playWin, resumeAudio } from '../utils/sounds';
import { randomFloat, randomInt, weightedIndex } from '../utils/random';
import { isUniform } from '../utils/weights';
import { displayIndices } from '../utils/limits';
import { accessibleTextColor } from '../utils/colorContrast';
import { trackAnalyticsEvent } from '../utils/analytics';
import { useLang } from '../utils/i18n';

import { PALETTES, type PaletteName } from '../utils/palettes';

function easeOutQuint(t: number): number {
  return 1 - Math.pow(1 - t, 5);
}

export interface SpinWheelRef {
  spin: () => void;
  /**
   * The live canvas, for exporting the wheel as an image.
   *
   * Returned rather than having the component own a download: what a caller
   * wants around the wheel — a title, a caption, branding, nothing at all —
   * depends on where the image is going, and that is the page's decision.
   */
  getCanvas: () => HTMLCanvasElement | null;
}

interface SpinWheelProps {
  /** Selectable entries only — callers filter out weight-0 entries first. */
  names: string[];
  weights?: number[];
  /**
   * Per-entry segment colour, parallel to `names`. An empty slot falls back to
   * the default palette, so a partly-customised wheel still gets contrasting
   * colours for the segments the user did not set.
   */
  colors?: (string | undefined)[];
  /**
   * Optional per-entry image source, parallel to `names`.
   *
   * Images are presentation only: they never participate in selection or slice
   * geometry. Missing, loading, or invalid sources fall back to the text label.
   */
  images?: (string | undefined)[];
  /**
   * Optional wheel-level image displayed upright in the centre hub.
   *
   * Unlike `images`, this is decorative and is not associated with an entry,
   * so it has no effect on slice geometry, weighting, or winner selection.
   */
  centerImage?: string;
  /** Palette for segments with no colour of their own. */
  palette?: PaletteName;
  /** Rim weight drawn around the wheel. */
  rimStyle?: 'classic' | 'bold' | 'none';
  /**
   * Draw every slice the same size regardless of weight. Selection stays
   * weighted, and the wheel still stops inside the winner's drawn slice, so the
   * result is honest either way — only the visual encoding of the odds changes.
   */
  equalSegments?: boolean;
  isSpinning: boolean;
  setIsSpinning: (v: boolean) => void;
  onSpinComplete: (winner: string, winnerIndex: number) => void;
  soundEnabled?: boolean;
  spinDuration?: 'slow' | 'normal' | 'fast';
  fontFamily?: string;   // CSS font-family stack string
  fontWeight?: string;   // 'normal' | 'bold'
  fontSize?: 'auto' | 'small' | 'medium' | 'large';
  /** Skip the spin animation entirely and show the winner instantly. */
  reduceMotion?: boolean;
  /**
   * Grow the wheel to fill its parent instead of stopping at 600px.
   *
   * Opt-in because the other callers (embed iframe, linked wheels) size the wheel
   * themselves. In fill mode the wheel is also capped by viewport height, so it
   * grows with the screen without pushing past the bottom of it.
   */
  fill?: boolean;
}

const DEFAULT_FONT_STACK = "'Inter Variable', Inter, system-ui, sans-serif";

const DURATION_MAP = { slow: 8000, normal: 6000, fast: 4000 };
const LOGICAL_SIZE = 600;
/** Largest CSS size the wheel is drawn at in fill mode. */
const MAX_FILL_SIZE = 1400;
/** Cap on backing-store scale (relative to the 600 logical px), bounding canvas memory. */
const MAX_RENDER_SCALE = 4;
const WHEEL_RADIUS = 265;
const CX = LOGICAL_SIZE / 2;
const CY = LOGICAL_SIZE / 2;

/**
 * Below this arc width a label is unreadable at any font size and would just
 * render as a smear of overlapping glyphs, so it is omitted. The winner is
 * still announced in full by WinnerModal and recorded in the Results tab.
 */
const MIN_LABEL_ARC_RAD = (8 * Math.PI) / 180;

interface CachedSliceImage {
  image: HTMLImageElement;
  status: 'loading' | 'ready' | 'error';
}

/**
 * Draw the stationary centre hub. A ready custom image gets a larger circular
 * cover crop; while it is absent, loading, or invalid the original hub remains
 * as the visual fallback.
 */
function drawHub(ctx: CanvasRenderingContext2D, customImage?: HTMLImageElement): void {
  const hasCustomImage = !!customImage && customImage.naturalWidth > 0 && customImage.naturalHeight > 0;
  const hubR = WHEEL_RADIUS * (hasCustomImage ? 0.15 : 0.09);
  const gradient = ctx.createRadialGradient(CX - 2, CY - 2, 0, CX, CY, hubR);
  gradient.addColorStop(0, '#ffffff');
  gradient.addColorStop(1, '#e2e8f0');

  ctx.beginPath();
  ctx.arc(CX, CY, hubR, 0, 2 * Math.PI);
  ctx.fillStyle = gradient;
  ctx.fill();

  if (hasCustomImage && customImage) {
    const sourceSide = Math.min(customImage.naturalWidth, customImage.naturalHeight);
    const sourceX = (customImage.naturalWidth - sourceSide) / 2;
    const sourceY = (customImage.naturalHeight - sourceSide) / 2;
    const inset = 3;

    ctx.save();
    ctx.beginPath();
    ctx.arc(CX, CY, hubR - inset, 0, 2 * Math.PI);
    ctx.clip();
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(
      customImage,
      sourceX,
      sourceY,
      sourceSide,
      sourceSide,
      CX - hubR + inset,
      CY - hubR + inset,
      (hubR - inset) * 2,
      (hubR - inset) * 2,
    );
    ctx.restore();

    // A pale inner ring separates light logos from light wheel segments.
    ctx.beginPath();
    ctx.arc(CX, CY, hubR - 1.5, 0, 2 * Math.PI);
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 3;
    ctx.stroke();
  } else {
    // Preserve the original centre pin when no usable custom image is ready.
    ctx.beginPath();
    ctx.arc(CX, CY, hubR * 0.38, 0, 2 * Math.PI);
    ctx.fillStyle = '#94a3b8';
    ctx.fill();
  }

  ctx.beginPath();
  ctx.arc(CX, CY, hubR, 0, 2 * Math.PI);
  ctx.strokeStyle = 'rgba(0,0,0,0.18)';
  ctx.lineWidth = 2;
  ctx.stroke();
}

/**
 * Slice widths in radians. Proportional to weight unless the caller asked for
 * an equal split or every weight is identical (in which case both agree).
 */
function sliceAngles(count: number, weights: number[] | undefined, equal: boolean): number[] {
  const proportional =
    !equal && !!weights && weights.length === count && !isUniform(weights);
  if (proportional && weights) {
    const total = weights.reduce((s, w) => s + w, 0);
    if (total > 0) return weights.map(w => (w / total) * 2 * Math.PI);
  }
  return Array(count).fill((2 * Math.PI) / count);
}

const SpinWheel = forwardRef<SpinWheelRef, SpinWheelProps>(
  ({ names, weights, colors, images, centerImage, palette = 'classic', rimStyle = 'classic', equalSegments = false, isSpinning, setIsSpinning, onSpinComplete, soundEnabled = true, spinDuration = 'normal', fontFamily, fontWeight, fontSize, reduceMotion = false, fill = false }, ref) => {
    const { t } = useLang();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const rotationRef = useRef(0);
    const animFrameRef = useRef<number | null>(null);
    const isSpinningRef = useRef(false);
    const namesRef = useRef(names);
    const weightsRef = useRef(weights);
    const colorsRef = useRef(colors);
    const imagesRef = useRef(images);
    const centerImageRef = useRef(centerImage);
    const emptyLabelRef = useRef(t('wheel.canvasEmpty'));
    /** Decoded once per source; drawFrame only reads ready cache entries. */
    const imageCacheRef = useRef<Map<string, CachedSliceImage>>(new Map());
    /** Coalesces several image-load completions into one idle repaint. */
    const imageRepaintRef = useRef<number | null>(null);
    const paletteRef = useRef(palette);
    const rimRef = useRef(rimStyle);
    const equalSegmentsRef = useRef(equalSegments);
    /**
     * Which source index each drawn segment represents.
     *
     * Identity for lists at or under the cap. Above it, an evenly spaced sample
     * — recomputed only when the list length changes, so the wheel does not
     * visibly reshuffle between renders.
     */
    const sampleRef = useRef<number[]>(displayIndices(names.length));
    const sampleLenRef = useRef(names.length);
    if (sampleLenRef.current !== names.length) {
      sampleRef.current = displayIndices(names.length);
      sampleLenRef.current = names.length;
    }
    const soundRef = useRef(soundEnabled);
    const [displaySize, setDisplaySize] = useState(LOGICAL_SIZE);
    const [isFullscreen, setIsFullscreen] = useState(false);
    // INP: cache context and DPR so the hot rAF path avoids property lookups
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
    const dprRef = useRef(typeof window !== 'undefined' ? (window.devicePixelRatio || 1) : 1);

    const fontFamilyRef = useRef(fontFamily ?? DEFAULT_FONT_STACK);
    const fontWeightRef = useRef(fontWeight ?? 'bold');
    const fontSizeRef = useRef(fontSize ?? 'auto');

    namesRef.current = names;
    weightsRef.current = weights;
    colorsRef.current = colors;
    imagesRef.current = images;
    centerImageRef.current = centerImage;
    emptyLabelRef.current = t('wheel.canvasEmpty');
    paletteRef.current = palette;
    rimRef.current = rimStyle;
    equalSegmentsRef.current = equalSegments;
    soundRef.current = soundEnabled;
    fontFamilyRef.current = fontFamily ?? DEFAULT_FONT_STACK;
    fontWeightRef.current = fontWeight ?? 'bold';
    fontSizeRef.current = fontSize ?? 'auto';

    // Track container width to make canvas responsive (browser-only).
    //
    // FORCED REFLOW FIX: the old `updateSize()` helper read `container.clientWidth`
    // synchronously inside the ResizeObserver callback. Reading any layout property
    // (clientWidth, offsetWidth, getBoundingClientRect) after a DOM change forces the
    // browser to immediately re-run layout so it can return an accurate value — that is
    // what PageSpeed labels a "forced reflow" (31 ms in our profile).
    //
    // ResizeObserver already delivers the new box dimensions in `entry.contentRect`,
    // computed as part of the layout pass that fired the observer. Reading `.width` from
    // there is a pure memory read — zero layout work.
    useEffect(() => {
      if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') return;
      const maxSize = fill ? MAX_FILL_SIZE : LOGICAL_SIZE;
      const ro = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // contentRect.width is computed by the browser before the callback fires —
          // reading it here costs nothing; clientWidth would cost a full reflow.
          const available = entry.contentRect.width;
          const size = Math.min(available, maxSize);
          setDisplaySize(size > 0 ? size : LOGICAL_SIZE);
        }
      });
      if (containerRef.current) {
        ro.observe(containerRef.current);
        // Fire immediately with current size so the canvas is sized on mount
        // without waiting for the first ResizeObserver callback.
        const initial = containerRef.current.getBoundingClientRect().width;
        const size = Math.min(initial, maxSize);
        setDisplaySize(size > 0 ? size : LOGICAL_SIZE);
      }
      return () => ro.disconnect();
    }, [fill]);


    // Sync fullscreen icon with actual fullscreen state
    useEffect(() => {
      const handler = () => {
        setIsFullscreen(!!document.fullscreenElement);
      };
      document.addEventListener('fullscreenchange', handler);
      return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    const toggleFullscreen = useCallback(async () => {
      try {
        if (!document.fullscreenElement) {
          await containerRef.current?.requestFullscreen();
        } else {
          await document.exitFullscreen();
        }
      } catch { /* fullscreen API not supported */ }
    }, []);

    const setupCanvas = useCallback(() => {
      if (typeof window === 'undefined') return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      // dprRef is the scale applied to the 600-unit drawing space. Normally that
      // is just the device pixel ratio. In fill mode it also folds in how far the
      // wheel has been stretched past (or shrunk below) 600px, so the backing
      // store matches the displayed pixels and the wheel stays crisp when large.
      const renderScale = fill
        ? Math.min((displaySize / LOGICAL_SIZE) * dpr, MAX_RENDER_SCALE)
        : dpr;
      dprRef.current = renderScale;
      canvas.width = Math.round(LOGICAL_SIZE * renderScale);
      canvas.height = Math.round(LOGICAL_SIZE * renderScale);
      // Fill mode lets CSS (w-full h-full aspect-square) drive the displayed size.
      canvas.style.width = fill ? '100%' : `${displaySize}px`;
      canvas.style.height = fill ? '100%' : `${displaySize}px`;
      // Cache context after (re)sizing — getContext returns the same object
      // but caching the ref avoids the property lookup inside every rAF tick.
      ctxRef.current = canvas.getContext('2d');
    }, [displaySize, fill]);

    // Fallback when there is no segment under the pointer (empty wheel).
    const POINTER_DEFAULT = '#7c3aed';

    /**
     * Draw the pointer in `fill` — the colour of the segment it currently rests on.
     *
     * A teardrop "flapper": a round body tapering to a tip that rests on the rim,
     * with a soft top-to-bottom shade for depth, a small specular highlight, and
     * a pivot pin. The outline flips between white and near-black so the pointer
     * stays visible whatever the segment colour: a pale-yellow pointer with a
     * white outline would disappear into the page, a navy one with a dark outline
     * would too.
     */
    const drawPointer = (ctx: CanvasRenderingContext2D, fill: string = POINTER_DEFAULT) => {
      const r = 14;                              // body radius
      const bodyX = CX + WHEEL_RADIUS + 18;      // body centre; edge + outline = ~598, inside the 600-unit canvas
      const tipX = CX + WHEEL_RADIUS - 8;        // tip dips slightly onto the wheel
      const outline = /^#[0-9a-f]{6}$/i.test(fill) && accessibleTextColor(fill) !== '#ffffff'
        ? '#1a1a2e'
        : 'white';

      // Tangent points from the tip to the body circle give a clean teardrop.
      const phi = Math.acos(r / (bodyX - tipX));
      const shape = new Path2D();
      shape.moveTo(tipX, CY);
      shape.lineTo(bodyX + r * Math.cos(Math.PI + phi), CY + r * Math.sin(Math.PI + phi));
      shape.arc(bodyX, CY, r, Math.PI + phi, 3 * Math.PI - phi);
      shape.closePath();

      ctx.save();
      ctx.lineJoin = 'round';

      // Body + drop shadow.
      ctx.shadowColor = 'rgba(0,0,0,0.38)';
      ctx.shadowBlur = 8;
      ctx.shadowOffsetX = 2;
      ctx.shadowOffsetY = 3;
      ctx.fillStyle = fill;
      ctx.fill(shape);
      ctx.shadowColor = 'transparent';

      // Depth: light from above, a touch of shade below.
      const shade = ctx.createLinearGradient(0, CY - r, 0, CY + r);
      shade.addColorStop(0, 'rgba(255,255,255,0.38)');
      shade.addColorStop(0.5, 'rgba(255,255,255,0)');
      shade.addColorStop(1, 'rgba(0,0,0,0.20)');
      ctx.fillStyle = shade;
      ctx.fill(shape);

      ctx.strokeStyle = outline;
      ctx.lineWidth = 2.5;
      ctx.stroke(shape);

      // Specular highlight on the upper-left of the body, clipped to the shape.
      ctx.save();
      ctx.clip(shape);
      const spec = ctx.createRadialGradient(bodyX - 5, CY - 6, 0, bodyX - 5, CY - 6, 9);
      spec.addColorStop(0, 'rgba(255,255,255,0.65)');
      spec.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = spec;
      ctx.beginPath();
      ctx.arc(bodyX - 5, CY - 6, 9, 0, 2 * Math.PI);
      ctx.fill();
      ctx.restore();

      // Pivot pin.
      ctx.beginPath();
      ctx.arc(bodyX, CY, 5, 0, 2 * Math.PI);
      ctx.fillStyle = '#ffffff';
      ctx.fill();
      ctx.strokeStyle = outline === 'white' ? 'rgba(0,0,0,0.35)' : outline;
      ctx.lineWidth = 1.5;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(bodyX, CY, 2, 0, 2 * Math.PI);
      ctx.fillStyle = fill;
      ctx.fill();

      ctx.restore();
    };

    const drawFrame = useCallback((rotation: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      // Use cached context; fall back to getContext only if not yet set
      const ctx = ctxRef.current ?? canvas.getContext('2d');
      if (!ctx) return;

      const dpr = dprRef.current;
      ctx.save();
      ctx.scale(dpr, dpr);
      ctx.clearRect(0, 0, LOGICAL_SIZE, LOGICAL_SIZE);

      const sample = sampleRef.current;
      // Everything below draws the SAMPLE. For lists at or under the cap the
      // sample is the whole list, so this is a no-op for the common case.
      const currentNames = sample.map(i => namesRef.current[i]);
      const currentWeights = weightsRef.current
        ? sample.map(i => weightsRef.current![i])
        : undefined;
      const currentColors = colorsRef.current
        ? sample.map(i => colorsRef.current![i])
        : undefined;
      const currentImages = imagesRef.current
        ? sample.map(i => imagesRef.current![i])
        : undefined;
      const centerSource = centerImageRef.current;
      const cachedCenterImage = centerSource
        ? imageCacheRef.current.get(centerSource)
        : undefined;
      const readyCenterImage = cachedCenterImage?.status === 'ready'
        ? cachedCenterImage.image
        : undefined;
      const N = currentNames.length;

      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.22)';
      ctx.shadowBlur = 28;
      ctx.shadowOffsetY = 6;
      ctx.beginPath();
      ctx.arc(CX, CY, WHEEL_RADIUS, 0, 2 * Math.PI);
      ctx.fillStyle = '#fff';
      ctx.fill();
      ctx.restore();

      if (N === 0) {
        ctx.beginPath();
        ctx.arc(CX, CY, WHEEL_RADIUS, 0, 2 * Math.PI);
        ctx.fillStyle = '#f5f7fa';
        ctx.fill();
        ctx.strokeStyle = '#e2e8f0';
        ctx.lineWidth = 4;
        ctx.stroke();
        ctx.fillStyle = '#94a3b8';
        ctx.font = "bold 16px 'Inter Variable', Inter, system-ui, sans-serif";
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(emptyLabelRef.current, CX, centerSource ? CY + WHEEL_RADIUS * 0.28 : CY);
        // Empty wheels normally have no hub. If a custom centre image was
        // chosen, still show it so the upload has immediate visual feedback.
        if (centerSource) drawHub(ctx, readyCenterImage);
        drawPointer(ctx);
        ctx.restore();
        return;
      }

      const angles = sliceAngles(N, currentWeights, equalSegmentsRef.current);

      // Compute cumulative start angles
      const startAngles: number[] = [];
      let cumAngle = rotation;
      for (let i = 0; i < N; i++) {
        startAngles.push(cumAngle);
        cumAngle += angles[i];
      }

      // The pointer sits at angle 0 (3 o'clock). Segment i spans
      // [rotation + sum(angles[0..i-1]), + angles[i]), so the one under the pointer
      // is where (0 - rotation) mod 2π falls in the running total. Resolved with the
      // same palette rule the slices use, so pointer and segment can never disagree.
      const TWO_PI = 2 * Math.PI;
      const pointerPos = (((-rotation) % TWO_PI) + TWO_PI) % TWO_PI;
      let pointerIdx = N - 1;
      let acc = 0;
      for (let i = 0; i < N; i++) {
        acc += angles[i];
        if (pointerPos < acc) { pointerIdx = i; break; }
      }
      const pointerPalette = PALETTES[paletteRef.current] ?? PALETTES.classic;
      const pointerColor = currentColors?.[pointerIdx] || pointerPalette[pointerIdx % pointerPalette.length];

      // Draw slices
      for (let i = 0; i < N; i++) {
        const startAngle = startAngles[i];
        const endAngle = startAngle + angles[i];
        ctx.beginPath();
        ctx.moveTo(CX, CY);
        ctx.arc(CX, CY, WHEEL_RADIUS, startAngle, endAngle);
        ctx.closePath();
        // A user-set colour wins; otherwise the palette slot for this position,
        // which is what guarantees adjacent segments never share a colour.
        const paletteColors = PALETTES[paletteRef.current] ?? PALETTES.classic;
        ctx.fillStyle = currentColors?.[i] || paletteColors[i % paletteColors.length];
        ctx.fill();
        // Below ~2° the separator is wider than the slice it separates, so it
        // stops reading as a divider and just greys the wheel out.
        if (angles[i] > 0.035) {
          ctx.strokeStyle = 'rgba(255,255,255,0.85)';
          ctx.lineWidth = 1.5;
          ctx.stroke();
        }
      }

      // Draw cached image thumbnails after the segment fills and before their
      // labels. Image decoding happens in an effect below, never in this hot rAF
      // path. Narrow slices skip thumbnails rather than painting an unreadable
      // sliver or multiplying drawImage work on large wheels.
      const imageDrawn = Array<boolean>(N).fill(false);
      const imageRadius = WHEEL_RADIUS * 0.45;
      for (let i = 0; i < N; i++) {
        const source = currentImages?.[i];
        if (!source) continue;
        const cached = imageCacheRef.current.get(source);
        if (!cached || cached.status !== 'ready') continue;

        const image = cached.image;
        if (image.naturalWidth <= 0 || image.naturalHeight <= 0) continue;

        // Keep the circle inside its wedge. The cap preserves useful detail on
        // broad slices; the floor suppresses tiny, expensive thumbnails.
        const diameter = Math.min(WHEEL_RADIUS * 0.29, imageRadius * angles[i] * 0.72);
        if (diameter < 18) continue;

        const midAngle = startAngles[i] + angles[i] / 2;
        const sourceSide = Math.min(image.naturalWidth, image.naturalHeight);
        const sourceX = (image.naturalWidth - sourceSide) / 2;
        const sourceY = (image.naturalHeight - sourceSide) / 2;

        ctx.save();
        ctx.translate(CX, CY);
        ctx.rotate(midAngle);

        // Circular cover crop: source aspect ratios never stretch, and the
        // subtle ring keeps pale images legible against pale segment colours.
        ctx.save();
        ctx.beginPath();
        ctx.arc(imageRadius, 0, diameter / 2, 0, 2 * Math.PI);
        ctx.clip();
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(
          image,
          sourceX,
          sourceY,
          sourceSide,
          sourceSide,
          imageRadius - diameter / 2,
          -diameter / 2,
          diameter,
          diameter,
        );
        ctx.restore();

        ctx.beginPath();
        ctx.arc(imageRadius, 0, diameter / 2, 0, 2 * Math.PI);
        ctx.strokeStyle = 'rgba(255,255,255,0.88)';
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.restore();
        imageDrawn[i] = true;
      }

      // Draw labels
      for (let i = 0; i < N; i++) {
        // A slice this narrow cannot show readable text — skip it rather than
        // draw glyphs that overlap the neighbouring slices.
        if (angles[i] < MIN_LABEL_ARC_RAD) continue;
        const midAngle = startAngles[i] + angles[i] / 2;
        ctx.save();
        ctx.translate(CX, CY);
        ctx.rotate(midAngle);
        const baseFontSize = (() => {
          const sizeMode = fontSizeRef.current;
          if (sizeMode === 'small') return N <= 5 ? 12 : N <= 10 ? 10 : N <= 16 ? 9 : N <= 25 ? 8 : 7;
          if (sizeMode === 'large') return N <= 5 ? 22 : N <= 10 ? 19 : N <= 16 ? 16 : N <= 25 ? 13 : 11;
          // 'auto' (and 'medium') — existing behavior
          return N <= 5 ? 17 : N <= 10 ? 15 : N <= 16 ? 13 : N <= 25 ? 11 : 9;
        })();
        const fw = fontWeightRef.current ?? 'bold';
        const ff = fontFamilyRef.current ?? DEFAULT_FONT_STACK;
        ctx.font = `${fw} ${baseFontSize}px ${ff}`;

        // WCAG 1.4.3 — dynamic text colour for 4.5:1 contrast against segment bg
        const paletteC = PALETTES[paletteRef.current] ?? PALETTES.classic;
        const segBg = currentColors?.[i] || paletteC[i % paletteC.length];
        const textColor = accessibleTextColor(segBg);
        ctx.fillStyle = textColor;

        ctx.textAlign = 'right';
        ctx.textBaseline = 'middle';
        // Reserve the inner radial band for a thumbnail when one was drawn.
        // Loading/failed images keep the original full-width text fallback.
        const maxWidth = WHEEL_RADIUS * (imageDrawn[i] ? 0.24 : 0.62);
        let text = currentNames[i];
        while (ctx.measureText(text).width > maxWidth && text.length > 1)
          text = text.slice(0, -1);
        if (text !== currentNames[i]) text = text.slice(0, -1) + '…';
        // Adaptive shadow: dark text gets a light shadow, light text a dark shadow
        ctx.shadowColor = textColor === '#ffffff' ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.35)';
        ctx.shadowBlur = 3;
        ctx.fillText(text, WHEEL_RADIUS * 0.87, 0);
        ctx.restore();
      }

      // Rim. 'none' still draws a hairline — without any edge the outermost
      // segments bleed into a white page and the wheel loses its silhouette.
      const rim = rimRef.current ?? 'classic';
      ctx.beginPath();
      ctx.arc(CX, CY, WHEEL_RADIUS, 0, 2 * Math.PI);
      ctx.strokeStyle = rim === 'bold' ? 'rgba(0,0,0,0.55)' : rim === 'none' ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.12)';
      ctx.lineWidth = rim === 'bold' ? 10 : rim === 'none' ? 1 : 4;
      ctx.stroke();

      // The hub is drawn in fixed canvas coordinates, so custom logos remain
      // upright while only the slice start angles rotate beneath them.
      drawHub(ctx, readyCenterImage);

      drawPointer(ctx, pointerColor);
      ctx.restore();
    }, []);

    // Decode each distinct source once. Keeping this work outside drawFrame is
    // essential: drawFrame runs on every animation frame for several seconds.
    useEffect(() => {
      if (typeof Image === 'undefined') return;

      const wanted = new Set(
        (images ?? []).filter((source): source is string => typeof source === 'string' && source.length > 0),
      );
      if (centerImage) wanted.add(centerImage);

      // Bound memory when entries are removed or their image is replaced.
      for (const [source, cached] of imageCacheRef.current) {
        if (wanted.has(source)) continue;
        cached.image.onload = null;
        cached.image.onerror = null;
        imageCacheRef.current.delete(source);
      }

      const scheduleRepaint = () => {
        if (isSpinningRef.current || imageRepaintRef.current !== null) return;
        imageRepaintRef.current = requestAnimationFrame(() => {
          imageRepaintRef.current = null;
          if (!isSpinningRef.current) drawFrame(rotationRef.current);
        });
      };

      for (const source of wanted) {
        if (imageCacheRef.current.has(source)) continue;

        const image = new Image();
        image.decoding = 'async';
        const cached: CachedSliceImage = { image, status: 'loading' };
        imageCacheRef.current.set(source, cached);

        image.onload = () => {
          // The source may have been removed while it was decoding.
          if (imageCacheRef.current.get(source) !== cached) return;
          cached.status = 'ready';
          scheduleRepaint();
        };
        image.onerror = () => {
          if (imageCacheRef.current.get(source) !== cached) return;
          cached.status = 'error';
        };
        image.src = source;
      }
    }, [images, centerImage, drawFrame]);

    useEffect(() => { setupCanvas(); drawFrame(rotationRef.current); }, [setupCanvas, drawFrame]);
    // Font values live in refs (so the rAF loop reads them without re-subscribing),
    // but refs never trigger a render — the font props must be listed here explicitly
    // or the canvas would not repaint until the next spin or entry change.
    // INP: debounce canvas redraws triggered by entry changes.
    //
    // Without debouncing, every character typed in the Bulk Edit textarea fires
    // setNames → React commit → this effect → drawFrame synchronously in the
    // same task as the keypress handler. On a 50-entry wheel, one drawFrame call
    // costs ~2–4 ms of main-thread time; at 60 wpm that is a continuous 8–16 ms
    // overhead per keypress, which is exactly what pushes INP above the 200 ms
    // "Good" threshold on mid-range devices.
    //
    // The fix: defer non-critical redraws 80 ms. This collapses a burst of
    // per-keystroke redraws (one per character) into a single draw at the end
    // of the burst, with zero visual difference for the user — the wheel is
    // below the fold while someone types in the Bulk Edit tab.
    //
    // During a spin, the rAF loop owns the canvas and calls drawFrame itself;
    // any data-change effect firing concurrently would fight the animation and
    // waste a frame. isSpinningRef guards against that.
    useEffect(() => {
      if (isSpinningRef.current) return; // rAF loop owns the canvas — skip
      const id = setTimeout(() => {
        if (!isSpinningRef.current) drawFrame(rotationRef.current);
      }, 80);
      return () => clearTimeout(id);
    }, [names, weights, colors, images, centerImage, palette, rimStyle, equalSegments, fontFamily, fontWeight, fontSize, drawFrame]);

    // Detect OS-level reduced motion preference — WCAG 2.3.1
    const prefersReducedMotion = typeof window !== 'undefined'
      && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const reduceMotionRef = useRef(reduceMotion);
    reduceMotionRef.current = reduceMotion;

    const spin = useCallback(() => {
      if (isSpinningRef.current || namesRef.current.length === 0) return;
      resumeAudio();
      isSpinningRef.current = true;
      setIsSpinning(true);

      // GA4 Tracking: Non-blocking wheel_spin event
      setTimeout(() => {
        trackAnalyticsEvent('wheel_spin', {
          event_category: 'engagement',
          event_label: 'Spin Wheel',
          entries_count: namesRef.current.length,
        });
      }, 50);

      // Yield to the main thread so the browser can paint the 'clicked' and
      // 'disabled' states of the Spin button before we lock it up with math.
      setTimeout(() => {
        const fullNames = namesRef.current;
        const fullWeights = weightsRef.current;
        const sample = sampleRef.current;
        // Slice geometry follows what is DRAWN.
        const currentNames = sample.map(i => fullNames[i]);
        const currentWeights = fullWeights ? sample.map(i => fullWeights[i]) : undefined;
        const N = currentNames.length;

        // Winner is chosen before the animation starts — the animation only
        // rotates the wheel to a result that is already decided.
        //
        // Selection runs over every entry, never the drawn sample: that is the
        // whole point of separating the two. A 50,000-name draw really is
        // 1-in-50,000 even though only 240 slices are ever painted.
        const fullN = fullNames.length;
        const useWeights = !!fullWeights && fullWeights.length === fullN && !isUniform(fullWeights);
        const sourceWinner = useWeights && fullWeights ? weightedIndex(fullWeights) : randomInt(fullN);
        if (sourceWinner < 0) {
          // Every weight was zero. Nothing is selectable; abandon the spin rather
          // than pick an entry the user deliberately took off the wheel.
          isSpinningRef.current = false;
          setIsSpinning(false);
          return;
        }

        // Place the winner into the visible sample so the pointer lands on the
        // name that actually won. Without this the wheel would stop on whatever
        // happened to be sampled into that slot — the animation would be lying.
        //
        // This has to happen before the slice widths below are measured, or with
        // weighting the aimed-at geometry would not match the repainted wheel.
        let winnerIdx = sample.indexOf(sourceWinner);
        if (winnerIdx === -1) {
          winnerIdx = randomInt(N);
          sample[winnerIdx] = sourceWinner;
          currentNames[winnerIdx] = fullNames[sourceWinner];
          if (currentWeights && fullWeights) currentWeights[winnerIdx] = fullWeights[sourceWinner];
          drawFrame(rotationRef.current);
        }

        // Drawn slice widths, measured after the swap so they describe the wheel
        // the user is actually looking at. Used only to work out where to stop.
        const angles = sliceAngles(N, currentWeights, equalSegmentsRef.current);

        // Compute start angles to determine target rotation
        const startAnglesArr: number[] = [];
        let cumAngle = 0;
        for (let i = 0; i < N; i++) {
          startAnglesArr.push(cumAngle);
          cumAngle += angles[i];
        }

        // Stop somewhere inside the middle 50% of the winner's slice rather than
        // dead centre every time. Still comfortably clear of both boundaries, so
        // the pointer can never appear to be sitting on the line between two
        // slices — which matters most for the narrow slices weighting produces.
        const landing = angles[winnerIdx] * (0.25 + randomFloat() * 0.5);
        const targetAngle = -(startAnglesArr[winnerIdx] + landing);

        const currentRotation = rotationRef.current;
        const normCurrent = ((currentRotation % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        const normTarget = ((targetAngle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
        let delta = normTarget - normCurrent;
        if (delta <= 0.01) delta += 2 * Math.PI;

        const minSpins = 6 + randomInt(4);
        const totalRotation = currentRotation + delta + minSpins * 2 * Math.PI;

        // WCAG 2.3.1 — skip animation when reduced motion is active
        if (reduceMotionRef.current || prefersReducedMotion) {
          rotationRef.current = totalRotation;
          drawFrame(totalRotation);
          isSpinningRef.current = false;
          setIsSpinning(false);
          if (soundRef.current) playWin();

          // GA4 Tracking: Non-blocking wheel_winner_announced event (Reduced Motion)
          setTimeout(() => {
            trackAnalyticsEvent('wheel_winner_announced', {
              event_category: 'engagement',
              event_label: 'Winner Announced',
            });
          }, 50);

          onSpinComplete(fullNames[sourceWinner], sourceWinner);
          return;
        }

        const duration = DURATION_MAP[spinDuration] + randomFloat() * 1000;
        const startRotation = currentRotation;

        // For tick sound: track cumulative angle
        let lastTickAngle = startRotation;
        const avgSliceAngle = (2 * Math.PI) / N;

        const startTime = performance.now();

        function animate(now: number) {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const eased = easeOutQuint(progress);
          const currentRot = startRotation + (totalRotation - startRotation) * eased;

          if (soundRef.current && N > 0) {
            const crossings = Math.floor((currentRot - lastTickAngle) / avgSliceAngle);
            if (crossings > 0) {
              const speed = (currentRot - lastTickAngle) / (elapsed || 1);
              playTick(speed * 100);
              lastTickAngle += crossings * avgSliceAngle;
            }
          }

          rotationRef.current = currentRot;
          drawFrame(currentRot);

          if (progress < 1) {
            animFrameRef.current = requestAnimationFrame(animate);
          } else {
            rotationRef.current = totalRotation;
            isSpinningRef.current = false;
            setIsSpinning(false);
            if (soundRef.current) playWin();

            // GA4 Tracking: Non-blocking wheel_winner_announced event
            setTimeout(() => {
              trackAnalyticsEvent('wheel_winner_announced', {
                event_category: 'engagement',
                event_label: 'Winner Announced',
              });
            }, 50);

            // The source index, not the drawn slot — callers index the full list.
            onSpinComplete(fullNames[sourceWinner], sourceWinner);
          }
        }

        animFrameRef.current = requestAnimationFrame(animate);
      }, 0);
    // `prefersReducedMotion` is a render-time constant from window.matchMedia,
    // not a reactive value — it cannot be stale, and adding it to deps would
    // recreate `spin` (and the imperative handle) on every render unnecessarily.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setIsSpinning, onSpinComplete, drawFrame, spinDuration]);

    useImperativeHandle(ref, () => ({
      spin,
      getCanvas: () => canvasRef.current,
    }), [spin]);

    useEffect(() => () => {
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (imageRepaintRef.current !== null) cancelAnimationFrame(imageRepaintRef.current);
      for (const cached of imageCacheRef.current.values()) {
        cached.image.onload = null;
        cached.image.onerror = null;
      }
      imageCacheRef.current.clear();
    }, []);

    // Dynamic aria-label describing the wheel state — WCAG 1.1.1 + 4.1.2
    const ariaLabel = useMemo(() => {
      const N = names.length;
      if (N === 0) return t('wheel.ariaEmpty');
      if (isSpinning) return t('wheel.ariaSpinning', { count: N });
      return t(N === 1 ? 'wheel.ariaReadyOne' : 'wheel.ariaReadyMany', { count: N });
    }, [names.length, isSpinning, t]);

    // Keyboard handler — Space/Enter fires spin from canvas focus
    const handleCanvasKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLCanvasElement>) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault(); // prevent page scroll on Space
          if (!isSpinning && names.length >= 1) spin();
        }
      },
      [isSpinning, names.length, spin],
    );

    return (
      <div
        ref={containerRef}
        className="relative w-full flex items-center justify-center aspect-square"
        style={isFullscreen
          ? { background: '#000', width: '100vw', height: '100vh' }
          // CLS fix: reserve square space before ResizeObserver fires so the
          // surrounding layout does not shift when the canvas reports its size.
          // contain:layout+paint stops the rAF spin loop from triggering ancestor
          // reflows on every frame, cutting layout cost during the animation.
          //
          // Fill mode: take the parent's width, but never be taller than the
          // viewport leaves room for. 14rem covers what sits above the wheel
          // (header, title row, card padding ≈ 10rem) plus the Spin button row
          // below it; the small "Projector mode" link under that may sit below
          // the fold. Because the box is square, capping width caps height too, so
          // the wheel can't overflow the screen on tall viewports.
          // The 600px floor means a short laptop screen never gets a wheel smaller
          // than the old fixed size; `100%` still wins on phones. margin:auto keeps
          // it centred when the viewport cap is the tighter limit.
          : fill
            ? {
                contain: 'layout paint',
                width: 'min(100%, max(600px, calc(100svh - 14rem)))',
                marginInline: 'auto',
              }
            : { contain: 'layout paint' }
        }
      >
        {/* Fullscreen toggle button */}
        <button
          onClick={toggleFullscreen}
          title={t(isFullscreen ? 'wheel.exitFullscreen' : 'wheel.enterFullscreen')}
          aria-label={t(isFullscreen ? 'wheel.exitFullscreen' : 'wheel.enterFullscreen')}
          className="absolute top-2 end-2 z-10 p-1.5 rounded-lg bg-white/80 dark:bg-gray-800/80 text-gray-500 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700 hover:text-gray-800 dark:hover:text-white shadow transition-all"
        >
          {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
        </button>

        <canvas
          id="wheel-canvas"
          ref={canvasRef}
          role="img"
          aria-label={ariaLabel}
          aria-describedby="wheel-static-desc"
          tabIndex={0}
          onKeyDown={handleCanvasKeyDown}
          width="600"
          height="600"
          className={`cursor-pointer select-none block focus-visible:outline-2 focus-visible:outline-violet-500 focus-visible:outline-offset-2 rounded-full${fill ? ' w-full h-full max-w-full aspect-square' : ''}`}
          style={{ touchAction: 'none', maxWidth: '100%' }}
          onClick={() => { if (!isSpinning) spin(); }}
        />

        {/* Permanently hidden description — screen readers read this once on focus */}
        <p id="wheel-static-desc" className="sr-only">
          {t('wheel.staticDesc')}
        </p>
      </div>
    );
  }
);

SpinWheel.displayName = 'SpinWheel';
export default SpinWheel;
